import { APP_LINK_HOSTS } from "./appLinks";

/** 앱 커스텀 스킴(flavor 별 prod `hamlak` · dev `hamlak-dev`, AC0-155). platform/links.ts APP_SCHEMES 와 같은 값. */
const APP_SCHEMES = ["hamlak:", "hamlak-dev:"];
/** 커스텀 스킴에서 다른 흐름이 소유하는 호스트 — 라우터로 보내지 않는다(auth = Apple 로그인 복귀, auth.native.ts). */
const RESERVED_HOSTS = new Set(["auth"]);
const HOME = "/home";

/**
 * 딥링크·앱 링크 URL → 라우터 경로(AC0-261). 두 형태를 같은 표로 읽는다:
 *   `hamlak://plots/<key>?src=…`  ·  `https://hamlak.com/plots/<key>?src=…`  → `/plots/<key>?src=…`
 *   `…/chat/<key>` → `/chat/<key>` · `…/home` → `/home` · `…/store` → `/store` · `…/go?plot=<key>` → `/plots/<key>`(나머지 쿼리 유지)
 *   빈 경로·모르는 경로 → `/home`
 * 우리 것이 아닌 URL(다른 스킴·다른 호스트·`hamlak://auth/…`)은 null — 호출부는 무시한다.
 */
export function deepLinkToPath(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  let segments: string[];
  if (APP_SCHEMES.includes(url.protocol)) {
    // `hamlak://plots/p1` → host 'plots', pathname '/p1'. (host 를 비우는 파서 대비 pathname 만으로도 같은 결과)
    const host = url.host.toLowerCase();
    segments = [...(host ? [host] : []), ...splitPath(url.pathname)];
    if (segments.length > 0 && RESERVED_HOSTS.has(segments[0]!)) return null;
  } else if (url.protocol === "https:" && APP_LINK_HOSTS.includes(url.hostname.toLowerCase())) {
    segments = splitPath(url.pathname);
  } else {
    return null;
  }

  const [head, key, ...rest] = segments;
  const params = new URLSearchParams(url.search);
  const withQuery = (path: string) => {
    const q = params.toString();
    return q ? `${path}?${q}` : path;
  };

  switch (head) {
    case undefined:
      return HOME;
    case "plots":
    case "chat": {
      if (!key || rest.length > 0) return HOME;
      const k = decodeSegment(key);
      return k ? withQuery(`/${head}/${encodeURIComponent(k)}`) : HOME;
    }
    case "home":
    case "store":
      return key === undefined ? withQuery(`/${head}`) : HOME;
    case "go":
      return key === undefined ? goPath(params) : HOME;
    default:
      return HOME;
  }
}

/** `/go?plot=<key>&…` → `/plots/<key>?…`(plot 외 쿼리 유지), plot 이 없으면 `/home`. 앱 링크 매핑과 `/go` 페이지(앱 안) 공용. */
export function goPath(search: URLSearchParams): string {
  const params = new URLSearchParams(search);
  const plot = params.get("plot");
  if (!plot) return HOME;
  params.delete("plot");
  const q = params.toString();
  return `/plots/${encodeURIComponent(plot)}${q ? `?${q}` : ""}`;
}

function splitPath(pathname: string): string[] {
  return pathname.split("/").filter((s) => s.length > 0);
}

function decodeSegment(s: string): string | null {
  try {
    return decodeURIComponent(s);
  } catch {
    return null;
  }
}
